import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

function MailBadgeIcon({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="m22 6-9.6 7.2a2 2 0 0 1-2.4 0L2 6"/>
    </svg>
  );
}

function UploadIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );
}

function DownloadIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function SearchIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function EmployeeRow({ emp, onSave, disabled }) {
  const [email, setEmail] = useState(emp.email || '');
  const [active, setActive] = useState(!!emp.notificaciones_activadas);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const markDirty = () => setDirty(true);

  const handleToggle = async (checked) => {
    if (checked && !email.trim()) {
      toast.error('Añade un email antes de activar las notificaciones', {
        description: `${emp.nombre} ${emp.apellidos}`,
      });
      return;
    }
    setActive(checked);
    setSaving(true);
    const ok = await onSave(emp.numero_personal, email.trim(), checked);
    setSaving(false);
    if (!ok) setActive(!checked);
    else setDirty(false);
  };

  const handleSaveEmail = async () => {
    setSaving(true);
    const ok = await onSave(emp.numero_personal, email.trim(), active);
    setSaving(false);
    if (ok) setDirty(false);
  };

  return (
    <tr data-testid={`notif-row-${emp.numero_personal}`} style={{ borderBottom: '1px solid #1e293b' }}>
      <td style={{ padding: '10px 12px', fontSize: '13px', color: '#e2e8f0' }}>
        <div style={{ fontWeight: 500 }}>{emp.nombre} {emp.apellidos}</div>
        <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'IBM Plex Mono' }}>{emp.numero_personal}</div>
      </td>
      <td style={{ padding: '10px 12px', fontSize: '12px', color: '#94a3b8' }}>{emp.desc_centro_coste || '—'}</td>
      <td style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Input
            data-testid={`notif-email-input-${emp.numero_personal}`}
            type="email"
            placeholder="empleado@ejemplo.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); markDirty(); }}
            disabled={disabled || saving}
            style={{ height: '32px', fontSize: '12px', minWidth: '220px' }}
          />
          {dirty && (
            <Button
              data-testid={`notif-save-email-btn-${emp.numero_personal}`}
              size="sm"
              variant="outline"
              onClick={handleSaveEmail}
              disabled={saving}
              style={{ height: '32px', fontSize: '11px', flexShrink: 0 }}
            >
              Guardar
            </Button>
          )}
        </div>
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
        <Switch
          data-testid={`notif-toggle-${emp.numero_personal}`}
          checked={active}
          disabled={disabled || saving}
          onCheckedChange={handleToggle}
        />
      </td>
    </tr>
  );
}

export default function NotificacionesPage() {
  const { apiFetch, user } = useAuth();
  const { t } = useLang();
  const fileInputRef = useRef(null);

  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const perPage = 25;
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [centro, setCentro]       = useState('all');
  const [centros, setCentros]     = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const isAdmin = user?.role === 'admin';

  const loadCentros = useCallback(async () => {
    try {
      const res = await apiFetch('/api/kpis/centros');
      if (res.ok) {
        const data = await res.json();
        setCentros(Array.isArray(data) ? data : []);
      }
    } catch (err) { console.warn('[SmartClockIn] silent catch:', err); }
  }, [apiFetch]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      if (search.trim()) params.set('search', search.trim());
      if (centro !== 'all') params.set('centro', centro);
      const res = await apiFetch(`/api/employees/notifications?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setRows(data.data || []);
        setTotal(data.total || 0);
      } else {
        toast.error('No se pudo cargar la lista de empleados');
      }
    } catch (err) {
      console.warn('[SmartClockIn] silent catch:', err);
      toast.error('Error de conexión');
    }
    setLoading(false);
  }, [apiFetch, page, search, centro]);

  useEffect(() => { loadCentros(); }, [loadCentros]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const handleSaveEmployee = async (numeroPersonal, email, active) => {
    try {
      const res = await apiFetch(`/api/employees/${numeroPersonal}/notifications`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, notificaciones_activadas: active }),
      });
      if (res.ok) {
        toast.success('Guardado correctamente');
        return true;
      }
      const data = await res.json().catch(() => ({}));
      toast.error(data.detail || 'No se pudo guardar');
      return false;
    } catch {
      toast.error('Error de conexión');
      return false;
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await apiFetch('/api/employees/notifications/template');
      if (!res.ok) { toast.error('No se pudo generar la plantilla'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_notificaciones.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch {
      toast.error('Error al descargar la plantilla');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch('/api/employees/notifications/import', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setImportResult(data);
        toast.success(`Importación completada: ${data.updated} actualizados`);
        loadRows();
      } else {
        toast.error(data.detail || 'Error al importar el archivo');
      }
    } catch {
      toast.error('Error de conexión al importar');
    }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <motion.div
      data-testid="notificaciones-page"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      style={{ padding: '28px 32px', maxWidth: '1280px' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '12px', flexShrink: 0,
            background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
          }}>
            <MailBadgeIcon size={26} />
          </div>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{t('notificaciones')}</h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0 0', maxWidth: '560px' }}>
              Configura qué empleados reciben un email al registrarse su entrada y su salida.
              El envío se dispara automáticamente en cada sincronización.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button
            data-testid="notif-download-template-btn"
            variant="outline"
            onClick={handleDownloadTemplate}
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
          >
            <DownloadIcon /> Descargar plantilla
          </Button>
          <Button
            data-testid="notif-import-btn"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
          >
            <UploadIcon /> {importing ? 'Importando...' : 'Importar Excel'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={handleImportFile}
            data-testid="notif-import-file-input"
          />
        </div>
      </div>

      {/* Import result banner */}
      {importResult && (
        <div
          data-testid="notif-import-result"
          style={{
            background: 'rgba(8,145,178,0.08)', border: '1px solid rgba(8,145,178,0.25)',
            borderRadius: '10px', padding: '14px 16px', marginBottom: '20px',
            fontSize: '13px', color: '#cbd5e1', display: 'flex', gap: '20px', flexWrap: 'wrap',
          }}
        >
          <span><strong style={{ color: '#22d3ee' }}>{importResult.updated}</strong> actualizados</span>
          {importResult.not_found > 0 && <span><strong style={{ color: '#f59e0b' }}>{importResult.not_found}</strong> no encontrados</span>}
          {importResult.skipped_rls > 0 && <span><strong style={{ color: '#f59e0b' }}>{importResult.skipped_rls}</strong> fuera de tu acceso</span>}
          {importResult.errors?.length > 0 && <span><strong style={{ color: '#ef4444' }}>{importResult.errors.length}</strong> errores</span>}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: '340px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none' }}>
            <SearchIcon size={15} />
          </span>
          <Input
            data-testid="notif-search-input"
            placeholder="Buscar por nombre, nº personal o email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ paddingLeft: '32px' }}
          />
        </div>
        <Select value={centro} onValueChange={(v) => { setCentro(v); setPage(1); }}>
          <SelectTrigger data-testid="notif-centro-filter" style={{ width: '260px' }}>
            <SelectValue placeholder="Todos los centros" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los centros</SelectItem>
            {centros.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0f172a' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Empleado</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Centro</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</th>
              <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notificar</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} style={{ padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>Cargando...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} style={{ padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '13px' }} data-testid="notif-empty-state">
                No hay empleados que coincidan con la búsqueda.
              </td></tr>
            )}
            {!loading && rows.map((emp) => (
              <EmployeeRow key={emp.numero_personal} emp={emp} onSave={handleSaveEmployee} disabled={false} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            {t('pagina')} {page} {t('de')} {totalPages} — {total} empleados
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button data-testid="notif-prev-page-btn" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
            <Button data-testid="notif-next-page-btn" size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
